/**
 * Unified OTP Component
 * Consolidates all OTP functionality from VerifyOTP.js, OTPSignup.js, and MagicLinkOnboarding.js
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  alpha,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { verifyOtpThunk, generateOtpThunk, updateTokens } from '../features/auth/authSlice';
import OtpInput from 'react-otp-input';
import axiosInstance from '../api/axiosInstance';
import { getPendingBooking, clearPendingBooking } from '../utils/bookingStorage';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  textTransform: 'none',
  fontSize: '1.1rem',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const StyledOtpInput = styled(OtpInput)(({ theme }) => ({
  '& input': {
    width: '2.8rem !important',
    height: '3.2rem',
    margin: '0 0.3rem',
    fontSize: '1.4rem',
    borderRadius: 12,
    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    caretColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.2s ease',
    '&:focus': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      outline: 'none',
      transform: 'scale(1.05)',
    },
    '&:not(:placeholder-shown)': {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    [theme.breakpoints.down('sm')]: {
      width: '2.5rem !important',
      height: '3rem',
      margin: '0 0.25rem',
      fontSize: '1.3rem',
    },
    [theme.breakpoints.down(380)]: {
      width: '2.2rem !important',
      height: '2.8rem',
      margin: '0 0.2rem',
      fontSize: '1.2rem',
    }
  },
}));

const UnifiedOTP = ({ 
  mode = 'verify',
  onSuccess,
  onError,
  identifier,
  userInfo,
  showSteps = false,
  title = "Almost There! 🚀",
  subtitle = "We've sent a 6-digit code to verify your account"
}) => {
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [lastPastedTime, setLastPastedTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    contactType: 'phone',
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({});
  
  const otpInputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateMagicLinkToken = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/v1/auth/magic-link/consume/', { 
        token,
        purpose: 'complete_profile'
      });
      
      if (response.data.success) {
        // Update authentication state
        dispatch(updateTokens({
          user: response.data.user,
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token
        }));
        
        toast.success('Welcome! Please complete your profile.');
        
        // Redirect based on user role and profile completion
        if (response.data.user?.role === 'patient') {
          // Check if patient profile needs completion
          if (!response.data.user?.patient_profile_completed && !response.data.user?.profile_completed) {
            // Redirect to patient onboarding
            navigate('/onboarding/patient/');
          } else {
            // Profile is complete, go to dashboard
            navigate('/patient-dashboard');
          }
        } else if (response.data.user?.role === 'doctor') {
          // Check if doctor needs onboarding
          if (!response.data.user?.profile_completed) {
            navigate('/doctor-onboarding-unified');
          } else {
            navigate('/doctor-dashboard');
          }
        } else {
          // Default: redirect to appropriate dashboard
          navigate(`/${response.data.user?.role}-dashboard`);
        }
      } else {
        setError('Invalid or expired magic link');
        toast.error('Invalid or expired magic link');
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      if (error.response?.status === 400) {
        setError('Invalid or expired magic link. Please request a new one.');
      } else if (error.response?.status === 404) {
        setError('Magic link not found. Please contact support.');
      } else {
        setError('Failed to process magic link. Please try again.');
      }
      toast.error('Failed to process magic link');
    } finally {
      setLoading(false);
    }
  }, [token, dispatch, navigate]);

  const handleVerifyOtp = useCallback(async (otpValue = otp) => {
    if (!otpValue || otpValue.length !== 6) {
      toast.info('Please enter the complete 6-digit code 🔢', {
        icon: '✨'
      });
      return;
    }

    try {
      setLoading(true);
      setError('');

      let result;
      const contact = mode === 'signup' ? formData.contact : identifier;

      if (mode === 'magic-link') {
        // Magic link verification is handled in validateMagicLinkToken
        // This should not be called for magic-link mode
        throw new Error('Magic link verification should be handled automatically');
      } else {
        result = await dispatch(verifyOtpThunk({ 
          identifier: contact, 
          otp: otpValue 
        })).unwrap();
      }

      toast.success('Verification successful! 🎉', {
        icon: '🌟'
      });

      // Check for pending booking after OTP verification
      const pendingBooking = getPendingBooking();
      if (pendingBooking && result.access && result.user) {
        try {
          const doctorId = pendingBooking.doctor_id || pendingBooking.doctorId;
          const bookingResponse = await axiosInstance.post(
            `/doctors/${doctorId}/book/`,
            {
              scheduled_date: pendingBooking.appointmentData?.scheduled_date || pendingBooking.selectedDate,
              scheduled_time: pendingBooking.appointmentData?.scheduled_time || pendingBooking.selectedTime,
              type: pendingBooking.appointmentData?.type || (pendingBooking.consultationType === 'video' ? 'Video' : pendingBooking.consultationType === 'phone' ? 'Phone' : 'In-Person'),
              details: pendingBooking.appointmentData?.details || pendingBooking.reason || '',
              // Include clinic ID if available
              ...(pendingBooking.clinic_id && { clinic_id: pendingBooking.clinic_id }),
            }
          );
          
          clearPendingBooking();
          toast.success('Appointment booked successfully!');
          
          setTimeout(() => {
            if (result.user?.role === 'patient') {
              navigate('/patient/appointments');
            } else {
              navigate(`/${result.user?.role}-dashboard`);
            }
          }, 1500);
          return;
        } catch (bookingErr) {
          console.error('Booking error after OTP verification:', bookingErr);
          toast.error('Failed to complete booking. Please try again.');
          clearPendingBooking();
        }
      }

      if (onSuccess) {
        onSuccess(result);
      } else {
        if (mode === 'signup') {
          navigate('/onboarding/patient/');
        } else if (mode === 'magic-link') {
          navigate('/doctor-dashboard');
        } else {
          if (result.user?.role === 'patient' && !result.user?.patient_profile_completed) {
            navigate('/onboarding/patient/');
          } else {
            navigate(`/${result.user?.role}-dashboard`);
          }
        }
      }
      
    } catch (error) {
      const errorMessage = error?.non_field_errors?.[0] || "That code didn't work";
      setError(errorMessage);
      toast.error(errorMessage, { 
        icon: '💭',
        onClose: () => {
          setOtp('');
          if (otpInputRef.current) {
            const firstInput = otpInputRef.current.querySelector('input');
            if (firstInput) firstInput.focus();
          }
        }
      });
    } finally {
      setLoading(false);
    }
  }, [otp, mode, formData.contact, identifier, onSuccess, navigate, dispatch]);

  // Resend timer effect
  useEffect(() => {
    let timer;
    if (resendTimer > 0 && !canResend) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [resendTimer, canResend]);

  // Auto-submit OTP when 6 digits entered
  useEffect(() => {
    if (otp.length === 6) {
      setTimeout(() => handleVerifyOtp(), 300);
    }
  }, [otp, handleVerifyOtp]);

  // Handle clipboard paste
  useEffect(() => {
    const handlePaste = async () => {
      try {
        const text = await navigator.clipboard.readText();
        const otpRegex = /^\d{6}$/;
        
        if (otpRegex.test(text)) {
          setOtp(text);
          toast.info('OTP pasted from clipboard! 📋', {
            icon: '✨',
            autoClose: 1500
          });
        }
      } catch (err) {
        console.log('Clipboard access not available');
      }
    };

    if (!otp && Date.now() - lastPastedTime > 2000) {
      handlePaste();
      setLastPastedTime(Date.now());
    }
  }, [otp, lastPastedTime]);

  // Auto focus on first input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (otpInputRef.current) {
        const firstInput = otpInputRef.current.querySelector('input');
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Magic link token validation
  useEffect(() => {
    if (mode === 'magic-link' && token) {
      validateMagicLinkToken();
    }
  }, [token, mode, validateMagicLinkToken]);

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Please enter your full name');
      return false;
    }
    if (!formData.contact.trim()) {
      setError('Please enter your phone number or email');
      return false;
    }
    
    if (formData.contactType === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact)) {
        setError('Please enter a valid email address');
        return false;
      }
    } else {
      if (formData.contact.length < 10) {
        setError('Please enter a valid phone number');
        return false;
      }
    }
    
    setError('');
    return true;
  };

  const validatePassword = () => {
    const newErrors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (mode === 'signup' && !validateForm()) return;

    try {
      setLoading(true);
      setError('');
      
      if (mode === 'signup') {
        const response = await fetch('/api/v1/auth/signup/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.contactType === 'phone' ? formData.contact : '',
            email: formData.contactType === 'email' ? formData.contact : '',
            role: 'patient',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create account');
        }
      }

      const contact = mode === 'signup' ? formData.contact : identifier;
      await dispatch(generateOtpThunk(contact)).unwrap();
      
      toast.success(`Verification code sent to ${contact}`);
      setActiveStep(1);
      setResendTimer(30);
      setCanResend(false);
      
    } catch (err) {
      console.error('Error sending OTP:', err);
      setError(err.message || 'Failed to send verification code');
      toast.error(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    try {
      const contact = mode === 'signup' ? formData.contact : identifier;
      await dispatch(generateOtpThunk(contact)).unwrap();
      toast.success("A fresh code is on its way to you! 📬", {
        icon: '✨'
      });
      setResendTimer(30);
      setCanResend(false);
    } catch (error) {
      if (error.type === 'USER_NOT_FOUND') {
        toast.error("Account not found. Please check your email/phone or create a new account.", {
          icon: '💭'
        });
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error(
          error?.non_field_errors?.[0] || 
          "Oops! We couldn't send a new code right now. Let's try again! 🔄",
          { icon: '💭' }
        );
      }
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);
  };

  const formatTime = (seconds) => {
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const maskedIdentifier = (contact) => {
    if (!contact) return '';
    return contact.includes('@') ?
      `${contact.split('@')[0].slice(0, 3)}...@${contact.split('@')[1]}` :
      `${contact.slice(0, 3)}...${contact.slice(-2)}`;
  };

  const renderSignupForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" gutterBottom>
        Create Your Account
      </Typography>
      
      <TextField
        fullWidth
        label="Full Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Enter your full name"
      />

      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Contact Method
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant={formData.contactType === 'phone' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setFormData({ ...formData, contactType: 'phone' })}
          >
            Phone
          </Button>
          <Button
            variant={formData.contactType === 'email' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setFormData({ ...formData, contactType: 'email' })}
          >
            Email
          </Button>
        </Box>
      </Box>

      <TextField
        fullWidth
        label={formData.contactType === 'phone' ? 'Phone Number' : 'Email Address'}
        value={formData.contact}
        onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
        placeholder={formData.contactType === 'phone' ? '+91XXXXXXXXXX' : 'your@email.com'}
        type={formData.contactType === 'phone' ? 'tel' : 'email'}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <StyledButton
        fullWidth
        variant="contained"
        onClick={handleSendOtp}
        disabled={loading || !formData.name || !formData.contact}
      >
        {loading ? <CircularProgress size={24} /> : 'Send Verification Code'}
      </StyledButton>
    </Box>
  );

  const renderPasswordForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" gutterBottom>
        Set Your Password
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose a strong password to secure your account.
      </Typography>

      <TextField
        fullWidth
        type="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!passwordErrors.password}
        helperText={passwordErrors.password || 'Minimum 8 characters'}
      />

      <TextField
        fullWidth
        type="password"
        label="Confirm Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!passwordErrors.confirmPassword}
        helperText={passwordErrors.confirmPassword}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <StyledButton
        fullWidth
        variant="contained"
        onClick={() => {
          if (validatePassword()) {
            setActiveStep(2);
          }
        }}
        disabled={!password || !confirmPassword}
      >
        Continue to Verification
      </StyledButton>
    </Box>
  );

  const renderOTPForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" gutterBottom>
        Verify Your Account
      </Typography>
      
      <Typography variant="body1" align="center" sx={{ mb: 2 }}>
        We've sent a 6-digit code to <strong>{maskedIdentifier(mode === 'signup' ? formData.contact : identifier)}</strong>
        <Box component="span" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.9rem' }}>
          Check your {(mode === 'signup' ? formData.contact : identifier)?.includes('@') ? 'email inbox' : 'phone'} for the verification code
        </Box>
      </Typography>

      <Box ref={otpInputRef} sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        mb: 4,
        width: '100%',
        maxWidth: '360px',
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        '& .otpContainer': {
          gap: { xs: 0.5, sm: 1 },
          width: '100%',
          justifyContent: 'space-between'
        }
      }}>
        <StyledOtpInput
          value={otp}
          onChange={handleOtpChange}
          numInputs={6}
          renderSeparator={<span style={{ width: '0px' }}></span>}
          renderInput={(props) => <input {...props} placeholder="•" inputMode="numeric" />}
          containerStyle="otpContainer"
          shouldAutoFocus={true}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        {!canResend && (
          <>
            <LinearProgress 
              variant="determinate" 
              value={(resendTimer / 30) * 100}
              sx={{ 
                height: 6, 
                borderRadius: 3,
                mb: 1,
                background: alpha(theme.palette.primary.main, 0.1),
              }} 
            />
            <Typography variant="body2" color="textSecondary" align="center">
              You can request a new code in {formatTime(resendTimer)}
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <StyledButton
          variant="contained"
          onClick={handleVerifyOtp}
          disabled={loading || otp.length !== 6}
          sx={{ 
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Verify & Continue'
          )}
        </StyledButton>

        <StyledButton
          variant="outlined"
          onClick={handleResendOtp}
          disabled={!canResend || loading}
          sx={{ 
            borderColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              borderColor: theme.palette.primary.main,
            }
          }}
        >
          {canResend ? "Send me a new code" : `Wait ${formatTime(resendTimer)}`}
        </StyledButton>
      </Box>
    </Box>
  );

  const steps = ['Enter Details', 'Set Password', 'Verify Account'];
  const currentStep = mode === 'magic-link' ? activeStep : (mode === 'signup' ? activeStep : 1);

  if (loading && mode === 'magic-link') {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" gap={2}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Processing your magic link...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && mode === 'magic-link') {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" gap={3} p={3}>
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Magic Link Error
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {error}
            </Typography>
          </Alert>
          
          <Box display="flex" gap={2}>
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>
      <Fade in timeout={1000}>
        <Box>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            gutterBottom 
            align="center"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 4
            }}
          >
            {title}
          </Typography>

          <StyledPaper>
            {showSteps && (
              <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {mode === 'signup' && activeStep === 0 && renderSignupForm()}
            {mode === 'magic-link' && activeStep === 0 && renderPasswordForm()}
            {(mode === 'verify' || activeStep === 1 || activeStep === 2) && renderOTPForm()}

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Having trouble? Don't worry! You can always{' '}
                <Button
                  color="primary"
                  onClick={() => navigate('/contact-support')}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    p: 0,
                    minWidth: 'auto',
                    verticalAlign: 'baseline',
                    '&:hover': {
                      background: 'transparent',
                      textDecoration: 'underline',
                    }
                  }}
                >
                  contact our support team
                </Button>
                {' '}for help.
              </Typography>
            </Box>
          </StyledPaper>
        </Box>
      </Fade>
    </Container>
  );
};

export default UnifiedOTP;